import { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import type { ColumnMapping, DataSource } from '../types';

// Import source detection helpers (mirrored from backend)
type ImportSource = 'costar' | 'crexi' | 'loopnet' | 'manual';

const COSTAR_DETECTION_FIELDS = [
  'Star Rating', 'PropertyID', 'Submarket Name', 'Market Name',
  'Building Park', 'Rentable Building Area', 'LEED Certified', 'Submarket Cluster',
];

const CREXI_DETECTION_FIELDS = [
  'Property Link', 'Crexi', 'USPS Vacancy', 'PFC Recording Date',
  'PFC Indicator', 'REO Sale Flag', 'Transaction Event Type', 'Mailing Address Care Of',
];

function detectImportSource(headers: string[]): ImportSource {
  const headerSet = new Set(headers.map(h => h.toLowerCase().trim()));

  const costarMatches = COSTAR_DETECTION_FIELDS.filter(f =>
    headerSet.has(f.toLowerCase())
  ).length;
  if (costarMatches >= 2) return 'costar';

  const crexiMatches = CREXI_DETECTION_FIELDS.filter(f =>
    headerSet.has(f.toLowerCase())
  ).length;
  if (crexiMatches >= 2) return 'crexi';

  if (headerSet.has('property link')) return 'crexi';

  return 'manual';
}

// CoStar column mappings (source column -> db field).
// Headers verified against actual CoStar Excel exports (May 2026):
//   * "CostarExport - Industrial / Retail" — 144-column For-Sale format
//   * "Costar - Sold Comps - Industrial" — 279-column Sold-Comps format
// Both formats use the same db fields where possible.
const COSTAR_MAPPINGS: Record<string, string[]> = {
  // Identity
  address: ['Address', 'Property Address', 'Property Location'],
  property_name: ['Name', 'Property Name', 'Building Name'],
  building_park: ['Building Park'],
  costar_id: ['PropertyID', 'CoStar ID', 'Property ID'],
  apn: ['Parcel Number 1 (Min)', 'APN', 'Parcel Number'],

  // Location
  city: ['City', 'Property City'],
  state: ['State', 'Property State'],
  zip: ['Zip', 'Property Zip', 'Property Zip Code', 'Zip Code'],
  county: ['County', 'County Name', 'Property County'],
  market: ['Market'],
  // Note: only map "Submarket"/"Submarket Name" — "Submarket Cluster" is a broader
  // grouping that goes into raw_import_data, not overwriting submarket.
  submarket: ['Submarket', 'Submarket Name'],
  cross_street: ['Cross Street'],
  latitude: ['Latitude'],
  longitude: ['Longitude'],

  // Classification
  property_type: ['Property Type'],
  property_subtype: ['Secondary Type'],
  building_class: ['Building Class'],
  building_status: ['Building Status', 'Constr Status'],
  zoning: ['Zoning'],

  // Size
  building_size: ['Size (SF)', 'Building SF', 'Rentable Building Area', 'Total Available Space (SF)'],
  land_area_sf: ['Land Area (SF)', 'Land Area SF'],
  lot_size_acres: ['Land Area (AC)', 'Land Area AC'],
  typical_floor_size: ['Typ Flr (SF)', 'Typical Floor (SF)'],
  number_of_floors: ['Number of Stories', 'Number Of Stories', 'Number of Floors'],
  number_of_units: ['Number of Units', 'Number Of Units'],
  floor_area_ratio: ['Floor Area Ratio'],
  coverage_ratio: ['Coverage'],

  // Building details
  year_built: ['Built', 'Year Built'],
  year_renovated: ['Renovated', 'Year Renovated'],
  construction_material: ['Construction Material'],
  // CoStar's "Building Materials" column is the exterior cladding (Reinforced
  // Concrete, Masonry, etc.) — a different concept from structural frame.
  building_materials_exterior: ['Building Materials'],
  building_condition: ['Building Condition'],
  roof_type: ['Roof Type'],
  heating: ['Heating'],
  lot_dimensions: ['Lot Dimensions'],

  // Industrial
  clear_height_ft: ['Ceiling Height', 'Ceiling Ht', 'Clear Height'],
  dock_doors: ['Loading Docks', 'Number Of Loading Docks'],
  grade_doors: ['Drive Ins', 'Grade Level Doors'],
  rail_served: ['Rail Served', 'Rail Lines'],
  column_spacing: ['Column Spacing'],
  // CoStar uses "Sprinklers" on For-Sale exports and "Fire Sprinkler" on Sold-Comp exports.
  sprinkler_type: ['Fire Sprinkler', 'Sprinklers'],
  number_of_cranes: ['Number of Cranes'],
  power: ['Power'],
  office_space: ['Office Space'],
  office_percentage: ['Percent Office'],

  // Parking / site
  parking_spaces: ['Number of Parking Spaces', 'Number Of Parking Spaces'],
  parking_ratio: ['Parking Ratio'],
  frontage_ft: ['Frontage'],
  corner_lot: ['Corner'],
  flood_zone: ['Flood Zone'],
  flood_risk: ['Flood Risk'],
  anchor_tenant: ['Anchor Tenants', 'Anchor Tenant'],

  // Leasing / occupancy
  percent_leased: ['Percent Leased'],
  // "Vacancy %" is the percentage; the bare "Vacancy" column is a raw count and
  // intentionally left unmapped.
  vacancy_percent: ['Vacancy %'],
  // For-Sale uses "Days On Market"; Sold-Comp uses "Market Time" (days from listing to close).
  days_on_market: ['Days On Market', 'Days on Market', 'Market Time'],
  tenancy: ['Tenancy'],
  number_of_tenants: ['Number of Tenants'],

  // Owner / fund
  // For-Sale exports give owner info on the property; map to owner_name (=true entity).
  owner_name: ['Owner Name', 'True Owner Name'],
  owner_recorded_name: ['Recorded Owner Name'],
  owner_address: ['Owner Address', 'True Owner Address'],
  fund_name: ['Fund Name', 'Acquisition Fund Name'],
  // "Parent Company" = corporate parent of the recorded LLC owner.
  parent_company: ['Parent Company'],
  // "Portfolio Name" = CoStar's named ownership portfolio (e.g. "Blackstone Industrial
  // Portfolio") — distinct from parent_company; gets its own column.
  portfolio_name: ['Portfolio Name'],

  // Property manager
  property_manager_name: ['Property Manager Name'],

  // Listing broker (CoStar exports broker info; mapped to leasing_company_* on properties
  // and to broker_company on transactions where applicable).
  leasing_company_name: ['Listing Broker Company', 'Leasing Company Name'],
  leasing_company_contact: ['Sale Company Contact'],
  leasing_company_phone: ['Listing Broker Phone'],

  // Transaction — sale price / cap rate
  // For active For-Sale listings, "Sale Price" = asking price.
  // For Sold Comps, "Sale Price" = closing price (transactions table treats both the same).
  sale_price: ['Sale Price', 'Last Sale Price', 'For Sale Price'],
  asking_price: ['Asking Price'],
  price_per_sf: ['Price Per SF', 'For Sale Price Per SF'],
  price_per_acre: ['Price Per AC Land'],
  // "Cap Rate" on For-Sale exports; "Actual Cap Rate" on Sold-Comp exports.
  cap_rate: ['Cap Rate', 'Actual Cap Rate'],
  pro_forma_cap_rate: ['Pro Forma Cap Rate'],

  // Transaction — dates / structure
  transaction_date: ['Sale Date', 'Last Sale Date'],
  recording_date: ['Recording Date'],
  document_number: ['Document Number'],
  sale_type: ['Sale Type'],
  sale_condition: ['Sale Condition'],
  hold_period_months: ['Hold Period'],
  transfer_tax: ['Transfer Tax'],
  // Listing status: "Active" / "Under Contract" / "Withdrawn" / "Sold". On
  // Sold Comps every row is "Sold" (redundant); on For-Sale exports it tells
  // you which listings are actually available.
  for_sale_status: ['Sale Status'],

  // Transaction — parties (Sold Comps)
  buyer_name: ['Buyer (True) Company'],
  buyer_recorded_name: ['Buyer (Recorded) Company'],
  buyer_type: ['Buyer (True) Type'],
  seller_name: ['Seller (True) Company'],
  seller_recorded_name: ['Seller (Recorded) Company'],
  seller_type: ['Seller (True) Type'],
  buyers_broker_company: ['Buyers Broker Company'],
  title_company: ['Title Company'],

  // Financing (first trust deed on Sold-Comp exports)
  lender: ['First Trust Deed Lender'],
  loan_amount: ['First Trust Deed Balance'],

  // Tax / assessed values
  improvement_value: ['Assessed Improved'],
  land_value: ['Assessed Land'],
  total_parcel_value: ['Assessed Value'],
  tax_year: ['Assessed Year'],

  // Utilities
  water: ['Water'],
  sewer: ['Sewer'],
  gas: ['Gas'],

  // Amenities / narrative
  amenities: ['Amenities'],
  description: ['Description Text'],
  notes: ['Transaction Notes'],
  legal_description: ['Legal Description'],
};

// Crexi column mappings
const CREXI_MAPPINGS: Record<string, string[]> = {
  address: ['Address'],
  city: ['City'],
  state: ['State'],
  zip: ['Zip Code'],
  county: ['County'],
  property_name: ['Property Name'],
  crexi_id: ['Property Link'],
  apn: ['APN'],
  property_type: ['Property Type'],
  zoning: ['Zoning Code'],
  latitude: ['Latitude'],
  longitude: ['Longitude'],
  building_size: ['Building SqFt'],
  lot_size_acres: ['Lot Size Acres'],
  land_area_sf: ['Lot Size SqFt'],
  number_of_units: ['Number of Units'],
  number_of_floors: ['Number of Stories'],
  year_built: ['Year Built'],
  percent_leased: ['Occupancy'],
  days_on_market: ['Days on Market'],
  owner_name: ['Owner Name'],
  owner_address: ['Mailing Address'],
  mailing_city: ['Mailing Address City'],
  mailing_state: ['Mailing Address State'],
  mailing_zip: ['Mailing Address Zip Code'],
  sale_price: ['Sold Price'],
  transaction_date: ['Sale Date'],
  price_per_sf: ['Sold Price/ SqFt'],
  asking_cap_rate: ['Asking Cap Rate'],
  cap_rate: ['Closing Cap Rate'],
  noi: ['Closing NOI'],
  lease_rate: ['Lease Rate'],
  lease_type: ['Lease Type'],
  tenant_name: ['Tenant(s)'],
  lender: ['Lender'],
  loan_amount: ['Loan Amount'],
  improvement_value: ['Improvement Value'],
  land_value: ['Land Value'],
  total_parcel_value: ['Total Parcel Value'],
  annual_tax_bill: ['Annual Tax Bill'],
};

// Fields to explicitly skip (no clean DB target OR known mis-mapping risk).
// IMPORTANT: removed from this list when we add a DB column for the field — e.g.
// "Tenancy" used to live here, now maps to master_properties.tenancy.
const SKIP_FIELDS = [
  // Geographic noise — always US/CA
  'Location Type', 'Continent', 'Subcontinent', 'Country', 'Region',
  // Sale Category / Investment Type rarely vary across CoStar exports — skip.
  // Note: "Sale Status" is NOT skipped — it maps to for_sale_status below.
  // It's redundant on Sold Comps (always "Sold") but critical on For-Sale exports
  // (Active / Under Contract / Withdrawn).
  'Sale Category', 'Investment Type',
  'Market Segment', 'Scale', 'Style', 'Star Rating', 'Rating',
  // Property-type-specific (hospitality, multifamily, affordable)
  'Affordable Type', 'Brand', 'Hotel Class', 'Hotel Location Type', 'Hotel Operator',
  'All-Inclusive', 'All-Suites', 'Pre-Leasing',
  '% 1-Bed', '% 2-Bed', '% 3-Bed', '% 4-Bed', '% Studios',
  'Studio Mix', 'One Bedroom Mix', 'Two Bedroom Mix', 'Three Bedroom Mix', 'Other Mix',
  'Number of Beds', 'Number of Rooms', 'Number of Studio Units',
  'Number of 1 Bedroom Units', 'Number of 2 Bedroom Units',
  'Number of 3 Bedroom Units', 'Number of Other Bedroom Units',
  'Avg Asking/Bed', 'Avg Asking/SF', 'Avg Asking/Unit', 'Avg Concessions %',
  'Avg Effective Rent/SF', 'Avg Effective Rent/Unit', 'Avg Unit SF',
  'Price Per Room', 'Price Per Unit', 'Units Per Acre',
  // Data center
  'Capacity - Total Utility kW', 'Capacity - Critical IT kW', 'Capacity - Available kW',
  'Data Center Type', 'Data Hall Area SF', 'Data Hall Count',
  'Power Usage Effectiveness', 'Data Center Tier', 'Power Redundancy',
  'Cooling Redundancy', 'Electric Utility', 'Density kW/SF', 'Density kW/rack',
  'Average Rental Rate Per kW', 'Price Per Total kW',
  // Lab space
  'Has Lab Space', 'Lab Space (SF)', 'Lab Space Percent Composition',
  // Sub-fields of address/owner already captured via the canonical field
  'Property Street Name', 'Property Street Number',
  'Property Street Post-Direction', 'Property Street Pre-Direction',
  'Buyer (True) Address', 'Buyer (True) City', 'Buyer (True) State',
  'Buyer (True) Zip Code', 'Buyer (True) Phone', 'Buyer (True) Contact Name',
  'Buyer (True) Post-Direction', 'Buyer (True) Pre-Direction',
  'Buyer (True) Street Name', 'Buyer (True) Street Number',
  'Buyer (True) Secondary Type', 'Buyer (True) Origin',
  'Buyer (Recorded) Address', 'Buyer (Recorded) City', 'Buyer (Recorded) State',
  'Buyer (Recorded) Zip Code', 'Buyer (Recorded) Phone',
  'Buyer (Recorded) Contact Name', 'Buyer (Recorded) Street Name',
  'Buyer (Recorded) Street Number', 'Buyer (Recorded) Street Post-Direction',
  'Buyer (Recorded) Street Pre-Direction',
  'Buyer (Contact) Address', 'Buyer (Contact) City', 'Buyer (Contact) State',
  'Buyer (Contact) Zip Code', 'Buyer (Contact) Phone',
  'Buyer (Contact) Contact Name', 'Buyer (Contact) Company',
  'Seller (True) Address', 'Seller (True) City', 'Seller (True) State',
  'Seller (True) Zip Code', 'Seller (True) Phone', 'Seller (True) Contact Name',
  'Seller (True) Post-Direction', 'Seller (True) Pre-Direction',
  'Seller (True) Street Name', 'Seller (True) Street Number',
  'Seller (True) Secondary Type', 'Seller (True) Origin',
  'Seller (Recorded) Address', 'Seller (Recorded) City', 'Seller (Recorded) State',
  'Seller (Recorded) Zip Code', 'Seller (Recorded) Phone',
  'Seller (Recorded) Contact Name', 'Seller (Recorded) Street Name',
  'Seller (Recorded) Street Number', 'Seller (Recorded) Street Post-Direction',
  'Seller (Recorded) Street Pre-Direction',
  'Seller (Contact) Address', 'Seller (Contact) City', 'Seller (Contact) State',
  'Seller (Contact) Zip Code', 'Seller (Contact) Phone',
  'Seller (Contact) Contact Name', 'Seller (Contact) Company',
  'Buyers Broker Address', 'Buyers Broker Agent First Name',
  'Buyers Broker Agent Last Name', 'Buyers Broker City', 'Buyers Broker Phone',
  'Buyers Broker State', 'Buyers Broker Street Name', 'Buyers Broker Street Number',
  'Buyers Broker Street Post-Direction', 'Buyers Broker Street Pre-Direction',
  'Buyers Broker Zip Code',
  'Listing Broker Address', 'Listing Broker Agent First Name',
  'Listing Broker Agent Last Name', 'Listing Broker City', 'Listing Broker State',
  'Listing Broker Street Name', 'Listing Broker Street Number',
  'Listing Broker Street Post-Direction', 'Listing Broker Street Pre-Direction',
  'Listing Broker Zip Code',
  // Portfolio sub-fields (only Portfolio Name → parent_company is useful)
  'Portfolio City', 'Portfolio County', 'Portfolio State', 'Portfolio Zip',
  'Disposition Fund Name',
  // Map / record-keeping (low business value)
  'Map Code', 'Map Page', 'Map X', 'Map Y', 'Fips Code', 'Stamp',
  'Comp ID', 'Comps Number', 'Parcel Number 2 (Max)', 'Multi-Sale Name',
  'Research Status', 'Sale Price Comment', 'Publication Date',
  'Non-Arms Length Reasons', 'Submarket Code', 'Submarket Cluster',
  // Derived / redundant pricing CoStar already publishes
  'Price Per AC Land Net', 'Price Per SF (Net)',
  'Price Per SF Land', 'Price Per SF Land Net', 'Price Per Land (SF)',
  // Land area sub-variants (we use Land Area (SF) / Land Area (AC))
  'Land Improvements', 'Land SF Gross', 'Land SF Net',
  // Lease rent category — handled via lease_type, not its own field
  'Rent Type',
  // Construction phasing / proposed use (not yet useful)
  'Construction Begin', 'Proposed Use',
  // Second trust deed detail (only first trust deed mapped for now)
  'Second Trust Deed Balance', 'Second Trust Deed Lender',
  'Second Trust Deed Payment', 'Second Trust Deed Terms',
  'First Trust Deed Payment', 'First Trust Deed Terms',
  // Misc / categorical with no DB home
  'Age', 'University', 'Down Payment', 'Gross Income', 'Net Income',
  'GIM', 'GRM', 'Improvement Ratio', 'Total Expense Amount',
  'Building Operating Expenses', 'Building Tax Expenses',
  'Size', // Sold-Comps duplicate of "Building SF"
  'Vacancy', // raw count; we map "Vacancy %" instead
  'Sprinklers', // duplicated column in Sold-Comps export; we use "Fire Sprinkler"
  'Green Rating', 'GreenRating',
];

interface AutoMapWarning {
  type: 'critical' | 'warning';
  message: string;
}

// Database fields that can be mapped to CSV columns
// Every field below corresponds to a real column in master_properties
// (verified against the live schema). Anything mapped here will be saved;
// anything else falls into raw_import_data JSON.
const DB_FIELDS: { field: string; label: string; category: string }[] = [
  // Basic Info
  { field: 'address', label: 'Property Address', category: 'Basic' },
  { field: 'unit_suite', label: 'Unit/Suite', category: 'Basic' },
  { field: 'property_name', label: 'Property Name', category: 'Basic' },
  { field: 'building_park', label: 'Building Park', category: 'Basic' },
  { field: 'apn', label: 'APN (Parcel Number)', category: 'Basic' },

  // Location
  { field: 'city', label: 'City', category: 'Location' },
  { field: 'state', label: 'State', category: 'Location' },
  { field: 'zip', label: 'Zip Code', category: 'Location' },
  { field: 'county', label: 'County', category: 'Location' },
  { field: 'market', label: 'Market', category: 'Location' },
  { field: 'submarket', label: 'Submarket', category: 'Location' },
  { field: 'cross_street', label: 'Cross Street', category: 'Location' },
  { field: 'latitude', label: 'Latitude', category: 'Location' },
  { field: 'longitude', label: 'Longitude', category: 'Location' },
  { field: 'opportunity_zone', label: 'Opportunity Zone', category: 'Location' },

  // Classification
  { field: 'property_type', label: 'Property Type', category: 'Classification' },
  { field: 'property_subtype', label: 'Property Subtype', category: 'Classification' },
  { field: 'building_status', label: 'Building Status', category: 'Classification' },
  { field: 'building_class', label: 'Building Class', category: 'Classification' },
  { field: 'zoning', label: 'Zoning', category: 'Classification' },

  // Size
  { field: 'building_size', label: 'Building Size (SF)', category: 'Size' },
  { field: 'land_area_sf', label: 'Lot Size (SF)', category: 'Size' },
  { field: 'lot_size_acres', label: 'Lot Size (Acres)', category: 'Size' },
  { field: 'typical_floor_size', label: 'Typical Floor Size (SF)', category: 'Size' },
  { field: 'number_of_buildings', label: 'Number of Buildings', category: 'Size' },
  { field: 'number_of_addresses', label: 'Number of Addresses', category: 'Size' },
  { field: 'floor_area_ratio', label: 'Floor Area Ratio (FAR)', category: 'Size' },
  { field: 'coverage_ratio', label: 'Coverage Ratio', category: 'Size' },
  { field: 'lot_dimensions', label: 'Lot Dimensions', category: 'Size' },

  // Building Details
  { field: 'year_built', label: 'Year Built', category: 'Details' },
  { field: 'year_renovated', label: 'Year Renovated', category: 'Details' },
  { field: 'number_of_floors', label: 'Number of Stories/Floors', category: 'Details' },
  { field: 'number_of_units', label: 'Number of Units', category: 'Details' },
  { field: 'construction_material', label: 'Construction Material (Structural)', category: 'Details' },
  { field: 'building_materials_exterior', label: 'Building Materials (Exterior)', category: 'Details' },
  { field: 'building_condition', label: 'Building Condition', category: 'Details' },
  { field: 'roof_type', label: 'Roof Type', category: 'Details' },
  { field: 'heating', label: 'Heating', category: 'Details' },
  { field: 'number_of_elevators', label: 'Number of Elevators', category: 'Details' },

  // Industrial
  { field: 'clear_height_ft', label: 'Clear Height (ft)', category: 'Industrial' },
  { field: 'dock_doors', label: 'Dock Doors', category: 'Industrial' },
  { field: 'grade_doors', label: 'Grade/Drive-In Doors', category: 'Industrial' },
  { field: 'rail_served', label: 'Rail Served', category: 'Industrial' },
  { field: 'column_spacing', label: 'Column Spacing', category: 'Industrial' },
  { field: 'sprinkler_type', label: 'Sprinklers / Sprinkler Type', category: 'Industrial' },
  { field: 'number_of_cranes', label: 'Number of Cranes', category: 'Industrial' },
  { field: 'power', label: 'Power', category: 'Industrial' },
  { field: 'office_space', label: 'Office Space (SF)', category: 'Industrial' },
  { field: 'office_percentage', label: 'Office %', category: 'Industrial' },

  // Retail / Parking / Site
  { field: 'parking_spaces', label: 'Parking Spaces', category: 'Retail/Site' },
  { field: 'parking_ratio', label: 'Parking Ratio', category: 'Retail/Site' },
  { field: 'anchor_tenant', label: 'Anchor Tenant(s)', category: 'Retail/Site' },
  { field: 'frontage_ft', label: 'Frontage (ft)', category: 'Retail/Site' },
  { field: 'corner_lot', label: 'Corner Lot', category: 'Retail/Site' },
  { field: 'flood_zone', label: 'Flood Zone', category: 'Retail/Site' },
  { field: 'flood_risk', label: 'Flood Risk', category: 'Retail/Site' },

  // Status / Occupancy
  { field: 'percent_leased', label: 'Percent Leased', category: 'Status' },
  { field: 'vacancy_percent', label: 'Vacancy %', category: 'Status' },
  { field: 'days_on_market', label: 'Days on Market / Market Time', category: 'Status' },
  { field: 'tenancy', label: 'Tenancy (Single / Multi)', category: 'Status' },
  { field: 'number_of_tenants', label: 'Number of Tenants', category: 'Status' },

  // Rent
  { field: 'rent_per_sf', label: 'Rent / SF', category: 'Rent' },
  { field: 'avg_weighted_rent', label: 'Avg Weighted Rent', category: 'Rent' },

  // Contacts/Owner
  { field: 'owner_name', label: 'Owner Name (True / Parent Entity)', category: 'Contacts' },
  { field: 'owner_recorded_name', label: 'Owner Name (Recorded / LLC on Deed)', category: 'Contacts' },
  { field: 'owner_contact', label: 'Owner Contact', category: 'Contacts' },
  { field: 'owner_phone', label: 'Owner Phone', category: 'Contacts' },
  { field: 'owner_address', label: 'Owner/Mailing Address', category: 'Contacts' },
  { field: 'mailing_city', label: 'Mailing City', category: 'Contacts' },
  { field: 'mailing_state', label: 'Mailing State', category: 'Contacts' },
  { field: 'mailing_zip', label: 'Mailing Zip', category: 'Contacts' },
  { field: 'mailing_care_of', label: 'Mailing Care Of', category: 'Contacts' },
  { field: 'parent_company', label: 'Parent Company (Corporate Parent of LLC)', category: 'Contacts' },
  { field: 'portfolio_name', label: 'Portfolio Name (CoStar Designation)', category: 'Contacts' },
  { field: 'fund_name', label: 'Fund Name', category: 'Contacts' },
  { field: 'property_manager_name', label: 'Property Manager', category: 'Contacts' },
  { field: 'property_manager_phone', label: 'Property Manager Phone', category: 'Contacts' },
  { field: 'leasing_company_name', label: 'Leasing/Listing Broker Company', category: 'Contacts' },
  { field: 'leasing_company_phone', label: 'Leasing/Broker Phone', category: 'Contacts' },
  { field: 'leasing_company_contact', label: 'Leasing/Broker Contact', category: 'Contacts' },
  { field: 'developer_name', label: 'Developer', category: 'Contacts' },
  { field: 'architect_name', label: 'Architect', category: 'Contacts' },

  // IDs & Links
  { field: 'costar_id', label: 'CoStar ID (PropertyID)', category: 'External IDs' },
  { field: 'crexi_id', label: 'Crexi ID / Property Link', category: 'External IDs' },

  // Transaction/Sale Fields
  { field: 'sale_price', label: 'Sale Price (or Asking on For-Sale)', category: 'Transaction' },
  { field: 'asking_price', label: 'Asking Price', category: 'Transaction' },
  { field: 'price_per_sf', label: 'Price / SF', category: 'Transaction' },
  { field: 'price_per_acre', label: 'Price / Acre', category: 'Transaction' },
  { field: 'asking_cap_rate', label: 'Asking Cap Rate', category: 'Transaction' },
  { field: 'cap_rate', label: 'Cap Rate (Actual)', category: 'Transaction' },
  { field: 'pro_forma_cap_rate', label: 'Pro Forma Cap Rate', category: 'Transaction' },
  { field: 'noi', label: 'NOI', category: 'Transaction' },
  { field: 'transaction_date', label: 'Sale / Transaction Date', category: 'Transaction' },
  { field: 'recording_date', label: 'Recording Date', category: 'Transaction' },
  { field: 'document_number', label: 'Document Number', category: 'Transaction' },
  { field: 'sale_type', label: 'Sale Type (Investment / Owner User)', category: 'Transaction' },
  { field: 'sale_condition', label: 'Sale Condition (Distressed / High Vacancy / etc.)', category: 'Transaction' },
  { field: 'hold_period_months', label: 'Hold Period (Months)', category: 'Transaction' },
  { field: 'transfer_tax', label: 'Transfer Tax', category: 'Transaction' },
  { field: 'buyer_name', label: 'Buyer (True / Parent Entity)', category: 'Transaction' },
  { field: 'buyer_recorded_name', label: 'Buyer (Recorded / LLC on Deed)', category: 'Transaction' },
  { field: 'buyer_type', label: 'Buyer Type', category: 'Transaction' },
  { field: 'seller_name', label: 'Seller (True / Parent Entity)', category: 'Transaction' },
  { field: 'seller_recorded_name', label: 'Seller (Recorded / LLC on Deed)', category: 'Transaction' },
  { field: 'seller_type', label: 'Seller Type', category: 'Transaction' },
  { field: 'buyers_broker_company', label: 'Buyers Broker Company', category: 'Transaction' },
  { field: 'title_company', label: 'Title Company', category: 'Transaction' },
  { field: 'for_sale_status', label: 'For-Sale Status', category: 'Transaction' },

  // Lease Fields
  { field: 'lease_type', label: 'Lease Type (NNN/Gross/etc)', category: 'Lease' },
  { field: 'lease_rate', label: 'Lease Rate', category: 'Lease' },
  { field: 'lease_term', label: 'Lease Term', category: 'Lease' },
  { field: 'lease_expiration_date', label: 'Lease Expiration Date', category: 'Lease' },
  { field: 'tenant_name', label: 'Tenant(s)', category: 'Lease' },

  // Financing
  { field: 'lender', label: 'Lender', category: 'Financing' },
  { field: 'loan_amount', label: 'Loan Amount', category: 'Financing' },
  { field: 'loan_type', label: 'Loan Type', category: 'Financing' },
  { field: 'interest_rate', label: 'Interest Rate', category: 'Financing' },
  { field: 'maturity_date', label: 'Maturity Date', category: 'Financing' },

  // Tax/Value Info
  { field: 'improvement_value', label: 'Improvement / Assessed Improved', category: 'Tax' },
  { field: 'land_value', label: 'Land Value / Assessed Land', category: 'Tax' },
  { field: 'total_parcel_value', label: 'Total Parcel / Assessed Value', category: 'Tax' },
  { field: 'parcel_value_type', label: 'Parcel Value Type', category: 'Tax' },
  { field: 'tax_year', label: 'Tax Year / Assessed Year', category: 'Tax' },
  { field: 'annual_tax_bill', label: 'Annual Tax Bill', category: 'Tax' },

  // Utilities
  { field: 'water', label: 'Water', category: 'Utilities' },
  { field: 'sewer', label: 'Sewer', category: 'Utilities' },
  { field: 'gas', label: 'Gas', category: 'Utilities' },

  // Amenities / Narrative
  { field: 'amenities', label: 'Amenities', category: 'Amenities' },
  { field: 'features', label: 'Features', category: 'Amenities' },
  { field: 'description', label: 'Property Description', category: 'Amenities' },
  { field: 'legal_description', label: 'Legal Description', category: 'Amenities' },
  { field: 'notes', label: 'Transaction / General Notes', category: 'Amenities' },
];

// Fallback exact-match hints for non-CoStar/Crexi imports.
// IMPORTANT: matching is now strict equality on normalized (lowercased, trimmed)
// header — substring matching caused mis-mappings like "Market" → days_on_market.
// Every key MUST be a real master_properties column.
const AUTO_MAP_HINTS: Record<string, string[]> = {
  // Identity
  address: ['address', 'property address', 'street address', 'property location', 'property street address'],
  unit_suite: ['unit', 'suite', 'unit/suite', 'unit suite', 'apt'],
  property_name: ['name', 'property name', 'building name'],
  building_park: ['building park', 'park name'],
  apn: ['apn', 'parcel number', 'assessor parcel', 'parcel id', 'parcel number 1 (min)'],

  // Location
  city: ['city', 'property city'],
  state: ['state', 'property state'],
  zip: ['zip', 'zip code', 'zipcode', 'postal code', 'property zip', 'property zip code'],
  county: ['county', 'county name', 'property county'],
  market: ['market'],
  submarket: ['submarket', 'submarket name'],
  cross_street: ['cross street'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'long', 'lng'],
  opportunity_zone: ['opportunity zone', 'oz'],

  // Classification
  property_type: ['property type', 'type', 'asset type'],
  property_subtype: ['secondary type', 'property subtype', 'subtype'],
  building_status: ['building status', 'constr status'],
  building_class: ['building class', 'class'],
  zoning: ['zoning'],

  // Size
  building_size: ['size (sf)', 'rentable building area', 'rba', 'building size', 'building sf', 'total available space (sf)'],
  land_area_sf: ['land area (sf)', 'land area sf', 'land sf', 'lot sf'],
  lot_size_acres: ['land area (ac)', 'lot size', 'acres', 'lot acres'],
  typical_floor_size: ['typ flr (sf)', 'typical floor size'],
  number_of_buildings: ['number of buildings'],
  number_of_addresses: ['number of addresses', 'address count'],

  // Building details
  year_built: ['built', 'year built'],
  year_renovated: ['renovated', 'year renovated'],
  number_of_floors: ['number of stories', 'number of floors', 'stories', 'floors'],
  number_of_units: ['number of units', 'units', 'unit count'],
  construction_material: ['construction material'],
  building_materials_exterior: ['building materials'],
  building_condition: ['building condition'],
  roof_type: ['roof type'],
  heating: ['heating'],
  number_of_elevators: ['number of elevators'],

  // Industrial
  clear_height_ft: ['ceiling height', 'ceiling ht', 'clear height'],
  dock_doors: ['loading docks', 'number of loading docks', 'dock doors', 'docks'],
  grade_doors: ['drive ins', 'grade level doors', 'grade doors'],
  rail_served: ['rail served', 'rail lines'],
  column_spacing: ['column spacing'],
  sprinkler_type: ['sprinklers', 'sprinkler type'],
  number_of_cranes: ['number of cranes'],
  power: ['power'],
  office_space: ['office space'],
  office_percentage: ['percent office', 'office %', 'office percent'],

  // Parking / site
  parking_spaces: ['number of parking spaces', 'parking spaces'],
  parking_ratio: ['parking ratio'],
  anchor_tenant: ['anchor tenant', 'anchor tenants'],
  frontage_ft: ['frontage', 'frontage ft', 'frontage (ft)'],
  corner_lot: ['corner', 'corner lot'],
  flood_zone: ['flood zone'],
  flood_risk: ['flood risk'],
  floor_area_ratio: ['floor area ratio', 'far'],
  coverage_ratio: ['coverage', 'coverage ratio'],
  lot_dimensions: ['lot dimensions'],

  // Status
  percent_leased: ['percent leased', 'leased %'],
  vacancy_percent: ['vacancy %', 'vacancy percent'],
  days_on_market: ['days on market', 'dom', 'market time'],
  tenancy: ['tenancy'],
  number_of_tenants: ['number of tenants'],

  // Rent
  rent_per_sf: ['rent per sf', 'rent/sf'],
  avg_weighted_rent: ['avg weighted rent', 'average rent'],

  // Owner / fund
  owner_name: ['owner name', 'owner', 'property owner', 'true owner name'],
  owner_recorded_name: ['recorded owner name'],
  owner_contact: ['owner contact'],
  owner_phone: ['owner phone'],
  owner_address: ['owner address', 'true owner address'],
  mailing_city: ['mailing city', 'mailing address city'],
  mailing_state: ['mailing state', 'mailing address state'],
  mailing_zip: ['mailing zip', 'mailing address zip'],
  mailing_care_of: ['mailing care of'],
  parent_company: ['parent company'],
  portfolio_name: ['portfolio name'],
  fund_name: ['fund name'],

  // Property manager / broker
  property_manager_name: ['property manager', 'property manager name'],
  property_manager_phone: ['property manager phone'],
  leasing_company_name: ['listing broker company', 'leasing company name'],
  leasing_company_contact: ['sale company contact', 'leasing contact'],
  leasing_company_phone: ['listing broker phone', 'leasing company phone'],

  // External IDs
  costar_id: ['propertyid', 'property id', 'costar id'],
  crexi_id: ['crexi id', 'property link'],

  // Transaction
  sale_price: ['sale price', 'sold price', 'last sale price', 'for sale price'],
  asking_price: ['asking price'],
  price_per_sf: ['price per sf', 'for sale price per sf', 'sold price/sqft', 'price/sf', '$/sf'],
  price_per_acre: ['price per acre', 'price per ac land', 'sold price/acre', '$/acre'],
  cap_rate: ['cap rate', 'actual cap rate'],
  asking_cap_rate: ['asking cap rate'],
  pro_forma_cap_rate: ['pro forma cap rate'],
  noi: ['noi', 'net operating income'],
  transaction_date: ['sale date', 'transaction date', 'closing date', 'sold date', 'last sale date'],
  recording_date: ['recording date'],
  document_number: ['document number'],
  sale_type: ['sale type'],
  sale_condition: ['sale condition'],
  hold_period_months: ['hold period', 'hold period months'],
  transfer_tax: ['transfer tax'],
  buyer_name: ['buyer', 'buyer name', 'buyer (true) company'],
  buyer_recorded_name: ['buyer (recorded) company'],
  buyer_type: ['buyer type', 'buyer (true) type'],
  seller_name: ['seller', 'seller name', 'seller (true) company'],
  seller_recorded_name: ['seller (recorded) company'],
  seller_type: ['seller type', 'seller (true) type'],
  buyers_broker_company: ['buyers broker company'],
  title_company: ['title company'],
  for_sale_status: ['sale status', 'for sale status', 'listing status'],

  // Lease
  lease_type: ['lease type', 'lease structure'],
  lease_rate: ['lease rate', 'rent'],
  lease_term: ['lease term'],
  lease_expiration_date: ['lease expiration', 'lease expiration date', 'expiration date'],
  tenant_name: ['tenant', 'tenants', 'tenant name', 'tenant(s)'],

  // Financing
  lender: ['lender', 'lender name', 'first trust deed lender'],
  loan_amount: ['loan amount', 'loan', 'first trust deed balance'],
  loan_type: ['loan type'],
  interest_rate: ['interest rate'],
  maturity_date: ['maturity date', 'financing maturity'],

  // Tax
  improvement_value: ['improvement value', 'assessed improved'],
  land_value: ['land value', 'assessed land'],
  total_parcel_value: ['total parcel value', 'parcel value', 'assessed value'],
  parcel_value_type: ['parcel value type'],
  tax_year: ['tax year', 'assessed year'],
  annual_tax_bill: ['annual tax', 'tax bill', 'annual tax bill'],

  // Utilities
  water: ['water'],
  sewer: ['sewer'],
  gas: ['gas'],

  // Amenities / narrative
  amenities: ['amenities'],
  features: ['features'],
  description: ['description', 'description text', 'property description'],
  legal_description: ['legal description'],
  notes: ['notes', 'transaction notes', 'comp notes'],
};

interface BulkUploadProps {
  onComplete?: () => void;
}

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';

interface ParsedCSV {
  headers: string[];
  rows: Record<string, string>[];
}

export default function BulkUpload({ onComplete }: BulkUploadProps) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<DataSource>('costar');
  const [parsedData, setParsedData] = useState<ParsedCSV | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    imported: number;
    updated: number;
    unchanged: number;
    skipped: number;
    errors: number;
    blocked_fields: number;
    error_details?: { row: number; error: string }[];
    blocked_details?: { row: number; field: string; existing_value: string | null; incoming_value: string | null; reason: string }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detectedSource, setDetectedSource] = useState<ImportSource | null>(null);
  const [autoMapWarnings, setAutoMapWarnings] = useState<AutoMapWarning[]>([]);
  const [unmappedCount, setUnmappedCount] = useState(0);

  // Detect the delimiter used in the CSV
  const detectDelimiter = useCallback((text: string): string => {
    const firstLine = text.split(/\r?\n/)[0] || '';

    // Count occurrences of common delimiters (outside of quotes)
    const delimiters = [',', '\t', ';', '|'];
    const counts: Record<string, number> = {};

    for (const delim of delimiters) {
      let count = 0;
      let inQuotes = false;
      for (const char of firstLine) {
        if (char === '"') inQuotes = !inQuotes;
        if (char === delim && !inQuotes) count++;
      }
      counts[delim] = count;
    }

    // Return delimiter with highest count
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    console.log('[BulkUpload] Delimiter detection:', counts, 'Selected:', best?.[0] || ',');
    return best && best[1] > 0 ? best[0] : ',';
  }, []);

  // Parse a single CSV line (handles quoted values)
  const parseCSVLine = useCallback((line: string, delimiter: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        if (nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = false;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());

    return result;
  }, []);

  // Parse CSV file
  const parseCSV = useCallback((text: string): ParsedCSV => {
    // Remove BOM if present
    const cleanText = text.replace(/^\uFEFF/, '');

    const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) {
      throw new Error('Empty file');
    }

    // Auto-detect delimiter
    const delimiter = detectDelimiter(cleanText);
    console.log('[BulkUpload] Using delimiter:', JSON.stringify(delimiter), 'Lines:', lines.length);

    // Parse header row
    const headers = parseCSVLine(lines[0], delimiter);
    console.log('[BulkUpload] Headers found:', headers.length, headers.slice(0, 5));

    if (headers.length <= 1) {
      throw new Error(`Could not parse columns. Found only ${headers.length} column(s). The file may use an unsupported format.`);
    }

    // Parse data rows
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      // Be more lenient - accept rows that are close to header count
      if (values.length >= headers.length - 2 && values.length <= headers.length + 2) {
        const row: Record<string, string> = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        rows.push(row);
      }
    }

    console.log('[BulkUpload] Rows parsed:', rows.length);
    return { headers, rows };
  }, [detectDelimiter, parseCSVLine]);

  // Parse Excel file (XLSX/XLS)
  const parseExcel = useCallback((buffer: ArrayBuffer): ParsedCSV => {
    // cellDates:true makes XLSX return date cells as JS Date objects rather
    // than Excel serial numbers (e.g. 45434). Without this, dates like
    // "Sale Date" come through as the string "45434", get sent to the backend
    // as-is, fail to insert into Postgres DATE columns, and the row's
    // transaction is silently dropped.
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

    // Get first sheet
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      throw new Error('No sheets found in Excel file');
    }

    const worksheet = workbook.Sheets[firstSheetName];
    console.log('[BulkUpload] Parsing Excel sheet:', firstSheetName);

    // Convert to JSON with header row. raw:false ensures cells are formatted
    // strings (matching what the user sees in Excel) — combined with cellDates,
    // dates land as ISO date strings the backend can parse.
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1, // Use first row as headers
      defval: '', // Default value for empty cells
      raw: false, // formatted values for non-date cells, ISO for dates
    }) as unknown[][];

    if (jsonData.length === 0) {
      throw new Error('Empty spreadsheet');
    }

    // First row is headers
    const headers = (jsonData[0] as unknown[]).map(h => String(h || '').trim()).filter(h => h);
    console.log('[BulkUpload] Excel headers found:', headers.length, headers.slice(0, 5));

    if (headers.length === 0) {
      throw new Error('No column headers found in first row');
    }

    // Remaining rows are data
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < jsonData.length; i++) {
      const rowData = jsonData[i] as unknown[];
      if (!rowData || rowData.length === 0) continue;

      // Check if row has any data
      const hasData = rowData.some(cell => cell !== null && cell !== undefined && cell !== '');
      if (!hasData) continue;

      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        const value = rowData[idx];
        row[header] = value !== null && value !== undefined ? String(value) : '';
      });
      rows.push(row);
    }

    console.log('[BulkUpload] Excel rows parsed:', rows.length);
    return { headers, rows };
  }, []);

  // Auto-map columns based on header names and detected source
  const autoMapColumns = useCallback((headers: string[], sourceType: ImportSource): {
    mappings: ColumnMapping[];
    warnings: AutoMapWarning[];
    unmapped: number;
  } => {
    const warnings: AutoMapWarning[] = [];
    const mappedHeaders = new Set<string>();

    // Choose mapping based on source
    const sourceMappings = sourceType === 'crexi' ? CREXI_MAPPINGS : COSTAR_MAPPINGS;

    const mappings: ColumnMapping[] = headers.map(header => {
      // Skip explicitly blocked fields
      if (SKIP_FIELDS.includes(header)) {
        return { csvColumn: header, dbField: null, sampleValues: [] };
      }

      const normalizedHeader = header.toLowerCase().trim();

      // First: Try source-specific mappings (exact match)
      let matchedField: string | null = null;
      for (const [dbField, sourceColumns] of Object.entries(sourceMappings)) {
        for (const sourceCol of sourceColumns) {
          if (normalizedHeader === sourceCol.toLowerCase().trim()) {
            matchedField = dbField;
            break;
          }
        }
        if (matchedField) break;
      }

      // Fallback: Try general AUTO_MAP_HINTS with STRICT exact-match only.
      // Substring matching caused mis-mappings (e.g. "Market" → days_on_market
      // because "days on market" contains "market"). Each hint must match the
      // normalized header exactly.
      if (!matchedField) {
        for (const [dbField, hints] of Object.entries(AUTO_MAP_HINTS)) {
          if (hints.some(hint => normalizedHeader === hint.toLowerCase().trim())) {
            matchedField = dbField;
            break;
          }
        }
      }

      if (matchedField) {
        mappedHeaders.add(header);
      }

      return {
        csvColumn: header,
        dbField: matchedField,
        sampleValues: [],
      };
    });

    // Generate warnings for required fields
    const hasAddress = mappings.some(m => m.dbField === 'address');
    const hasCity = mappings.some(m => m.dbField === 'city');
    const hasState = mappings.some(m => m.dbField === 'state');

    if (!hasAddress) {
      warnings.push({ type: 'critical', message: 'No address column found. Import will fail for all rows.' });
    }
    if (!hasCity) {
      warnings.push({ type: 'critical', message: 'No city column found. Import will fail for all rows.' });
    }
    if (!hasState) {
      warnings.push({ type: 'critical', message: 'No state column found. Import will fail for all rows.' });
    }

    // Check if Location Type was accidentally mapped to address
    const locationTypeMapping = mappings.find(m => m.csvColumn === 'Location Type' && m.dbField === 'address');
    if (locationTypeMapping) {
      warnings.push({ type: 'warning', message: '"Location Type" should NOT be mapped to address. This contains "Suburban/Urban/CBD" values, not street addresses.' });
      locationTypeMapping.dbField = null;
    }

    const unmapped = headers.filter(h => !mappedHeaders.has(h) && !SKIP_FIELDS.includes(h)).length;

    return { mappings, warnings, unmapped };
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);

    try {
      const fileName = selectedFile.name.toLowerCase();
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.xlsb');

      console.log('[BulkUpload] File type:', isExcel ? 'Excel' : 'CSV/Text', 'Name:', selectedFile.name);

      let parsed: ParsedCSV;

      if (isExcel) {
        // Parse Excel file
        const buffer = await selectedFile.arrayBuffer();
        parsed = parseExcel(buffer);
      } else {
        // Parse CSV/Text file
        const text = await selectedFile.text();
        parsed = parseCSV(text);
      }

      if (parsed.rows.length === 0) {
        throw new Error('No data rows found in file');
      }

      setParsedData(parsed);

      // Detect source type from headers
      const detected = detectImportSource(parsed.headers);
      setDetectedSource(detected);
      console.log('[BulkUpload] Detected source:', detected);

      // Auto-set source dropdown based on detection
      if (detected === 'costar') setSource('costar');
      else if (detected === 'crexi') setSource('crexi');

      // Auto-map columns using source-specific mappings
      const { mappings, warnings, unmapped } = autoMapColumns(parsed.headers, detected);
      const mappingsWithSamples = mappings.map(mapping => ({
        ...mapping,
        sampleValues: parsed.rows.slice(0, 3).map(row => row[mapping.csvColumn] || ''),
      }));

      setColumnMappings(mappingsWithSamples);
      setAutoMapWarnings(warnings);
      setUnmappedCount(unmapped);
      setStep('mapping');
    } catch (err) {
      console.error('[BulkUpload] Parse error:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    }
  }, [parseCSV, parseExcel, autoMapColumns]);

  // Update column mapping
  const updateMapping = useCallback((csvColumn: string, dbField: string | null) => {
    setColumnMappings(prev =>
      prev.map(m => (m.csvColumn === csvColumn ? { ...m, dbField } : m))
    );
  }, []);

  // Group mappings by category
  const groupedFields = useMemo(() => {
    const groups: Record<string, typeof DB_FIELDS> = {};
    DB_FIELDS.forEach(field => {
      if (!groups[field.category]) {
        groups[field.category] = [];
      }
      groups[field.category].push(field);
    });
    return groups;
  }, []);

  // Check if required fields are mapped
  const hasRequiredFields = useMemo(() => {
    const mappedFields = columnMappings.filter(m => m.dbField).map(m => m.dbField);
    return mappedFields.includes('address');
  }, [columnMappings]);

  // Handle import
  const handleImport = useCallback(async () => {
    if (!parsedData || !hasRequiredFields) return;

    setImporting(true);
    setError(null);

    try {
      // Build the import payload
      const mappingObj: Record<string, string> = {};
      columnMappings.forEach(m => {
        if (m.dbField) {
          mappingObj[m.csvColumn] = m.dbField;
        }
      });

      console.log('[BulkUpload] Mapping object:', mappingObj);
      console.log('[BulkUpload] Has address mapping:', Object.values(mappingObj).includes('address'));

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }
      console.log('[BulkUpload] Token exists:', !!token);

      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiBase}/master-properties/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          source,
          columnMapping: mappingObj,
          rows: parsedData.rows,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Import failed');
      }

      setImportResult({
        success: true,
        imported: result.imported || 0,
        updated: result.updated || 0,
        unchanged: result.unchanged || 0,
        skipped: result.skipped || 0,
        errors: result.errors || 0,
        blocked_fields: result.blocked_fields || 0,
        error_details: result.error_details || undefined,
        blocked_details: result.blocked_details || undefined,
      });
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [parsedData, columnMappings, source, hasRequiredFields]);

  // Reset and start over
  const handleReset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setColumnMappings([]);
    setImportResult(null);
    setError(null);
    setDetectedSource(null);
    setAutoMapWarnings([]);
    setUnmappedCount(0);
  }, []);

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {(['upload', 'mapping', 'preview', 'complete'] as const).map((s, idx) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === s
                  ? 'bg-primary-500 text-white'
                  : ['upload', 'mapping', 'preview', 'complete'].indexOf(step) > idx
                  ? 'bg-success text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {idx + 1}
            </div>
            {idx < 3 && <div className="w-12 h-0.5 bg-gray-200 mx-1" />}
          </div>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Property Data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Source Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Source
              </label>
              <select
                value={source}
                onChange={e => setSource(e.target.value as DataSource)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="costar">CoStar</option>
                <option value="crexi">Crexi</option>
                <option value="loopnet">LoopNet</option>
                <option value="mls">MLS</option>
                <option value="public_records">Public Records</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsb"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <span className="text-primary-500 font-medium">Click to upload</span>
                  <span className="text-gray-500"> or drag and drop</span>
                </div>
                <p className="text-xs text-gray-400">Excel (.xlsx, .xls) or CSV files</p>
              </label>
            </div>

            {file && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && parsedData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-3">
                  Map Columns
                  {detectedSource && (
                    <span className={`text-xs px-2 py-1 rounded-full font-normal ${
                      detectedSource === 'costar' ? 'bg-info-wash text-info' :
                      detectedSource === 'crexi' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      Detected: {detectedSource === 'costar' ? 'CoStar' : detectedSource === 'crexi' ? 'Crexi' : 'Manual'}
                    </span>
                  )}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {parsedData.rows.length} rows found. {columnMappings.filter(m => m.dbField).length} fields mapped, {unmappedCount} unmapped (optional).
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (parsedData) {
                    const { mappings, warnings, unmapped } = autoMapColumns(parsedData.headers, detectedSource || 'manual');
                    const mappingsWithSamples = mappings.map(mapping => ({
                      ...mapping,
                      sampleValues: parsedData.rows.slice(0, 3).map(row => row[mapping.csvColumn] || ''),
                    }));
                    setColumnMappings(mappingsWithSamples);
                    setAutoMapWarnings(warnings);
                    setUnmappedCount(unmapped);
                  }
                }}
              >
                Re-Apply Auto-Map
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Warnings */}
            {autoMapWarnings.length > 0 && (
              <div className="mb-4 space-y-2">
                {autoMapWarnings.map((w, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg text-sm ${
                      w.type === 'critical'
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-warning-wash border border-warning/30 text-warning'
                    }`}
                  >
                    <strong>{w.type === 'critical' ? 'CRITICAL: ' : 'Warning: '}</strong>
                    {w.message}
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {columnMappings.map(mapping => (
                <div
                  key={mapping.csvColumn}
                  className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  {/* CSV Column */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{mapping.csvColumn}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {mapping.sampleValues.filter(Boolean).slice(0, 2).join(' | ') || 'No data'}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="text-gray-400 pt-1">→</div>

                  {/* DB Field Selector */}
                  <div className="flex-1">
                    <select
                      value={mapping.dbField || ''}
                      onChange={e => updateMapping(mapping.csvColumn, e.target.value || null)}
                      className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                        mapping.dbField
                          ? 'border-success/30 bg-success-wash'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="">-- Skip this column --</option>
                      {Object.entries(groupedFields).map(([category, fields]) => (
                        <optgroup key={category} label={category}>
                          {fields.map(field => (
                            <option
                              key={field.field}
                              value={field.field}
                              disabled={columnMappings.some(
                                m => m.dbField === field.field && m.csvColumn !== mapping.csvColumn
                              )}
                            >
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {!hasRequiredFields && (
              <div className="mt-4 p-3 bg-warning-wash border border-warning/30 rounded-lg text-warning text-sm">
                <strong>Required:</strong> Please map the "Property Address" field to continue.
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={handleReset}>
                Start Over
              </Button>
              <Button
                onClick={() => setStep('preview')}
                disabled={!hasRequiredFields}
              >
                Preview Import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && parsedData && (
        <Card>
          <CardHeader>
            <CardTitle>Preview Import</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-info-wash p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-info">{parsedData.rows.length}</p>
                  <p className="text-sm text-info">Total Rows</p>
                </div>
                <div className="bg-success-wash p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-success">
                    {columnMappings.filter(m => m.dbField).length}
                  </p>
                  <p className="text-sm text-success">Fields Mapped</p>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-800">{source}</p>
                  <p className="text-sm text-gray-800">Data Source</p>
                </div>
              </div>

              {/* Mapped Fields Preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <p className="font-medium text-sm">Mapped Fields</p>
                </div>
                <div className="p-4 grid grid-cols-2 gap-2 text-sm">
                  {columnMappings
                    .filter(m => m.dbField)
                    .map(m => (
                      <div key={m.csvColumn} className="flex items-center gap-2">
                        <span className="text-gray-500">{m.csvColumn}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium">{m.dbField}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Sample Data Preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <p className="font-medium text-sm">Sample Data (First 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {columnMappings
                          .filter(m => m.dbField)
                          .slice(0, 6)
                          .map(m => (
                            <th key={m.csvColumn} className="px-3 py-2 text-left font-medium">
                              {m.dbField}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parsedData.rows.slice(0, 5).map((row, idx) => (
                        <tr key={idx}>
                          {columnMappings
                            .filter(m => m.dbField)
                            .slice(0, 6)
                            .map(m => (
                              <td key={m.csvColumn} className="px-3 py-2 truncate max-w-[200px]">
                                {row[m.csvColumn] || '-'}
                              </td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep('mapping')}>
                  Back to Mapping
                </Button>
                <Button onClick={handleImport} isLoading={importing}>
                  {importing ? 'Importing...' : `Import ${parsedData.rows.length} Properties`}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && importResult && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-success-wash rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
            <div className="flex justify-center gap-6 mb-6">
              <div>
                <p className="text-2xl font-bold text-success">{importResult.imported}</p>
                <p className="text-sm text-gray-500">Inserted</p>
              </div>
              {importResult.updated > 0 && (
                <div>
                  <p className="text-2xl font-bold text-info">{importResult.updated}</p>
                  <p className="text-sm text-gray-500">Updated</p>
                </div>
              )}
              {importResult.skipped > 0 && (
                <div>
                  <p className="text-2xl font-bold text-warning">{importResult.skipped}</p>
                  <p className="text-sm text-gray-500">Skipped</p>
                </div>
              )}
              {importResult.unchanged > 0 && (
                <div>
                  <p className="text-2xl font-bold text-gray-500">{importResult.unchanged}</p>
                  <p className="text-sm text-gray-500">Unchanged</p>
                </div>
              )}
              {importResult.blocked_fields > 0 && (
                <div>
                  <p className="text-2xl font-bold text-warning">{importResult.blocked_fields}</p>
                  <p className="text-sm text-gray-500">Fields Protected</p>
                </div>
              )}
              {importResult.errors > 0 && (
                <div>
                  <p className="text-2xl font-bold text-red-600">{importResult.errors}</p>
                  <p className="text-sm text-gray-500">Errors</p>
                </div>
              )}
            </div>
            {importResult.blocked_details && importResult.blocked_details.length > 0 && (
              <div className="max-w-lg mx-auto mb-6 text-left">
                <p className="text-sm font-medium text-warning mb-2">
                  Protected by source priority (kept the higher-authority value):
                </p>
                <div className="bg-warning-wash border border-warning/30 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.blocked_details.slice(0, 10).map((b, idx) => (
                    <p key={idx} className="text-xs text-warning">
                      <span className="font-medium">Row {b.row} - {b.field}:</span>{' '}
                      kept "{b.existing_value ?? ''}", ignored "{b.incoming_value ?? ''}"
                    </p>
                  ))}
                  {importResult.blocked_details.length > 10 && (
                    <p className="text-xs text-warning italic">
                      ...and {importResult.blocked_details.length - 10} more
                    </p>
                  )}
                </div>
              </div>
            )}
            {importResult.error_details && importResult.error_details.length > 0 && (
              <div className="max-w-lg mx-auto mb-6 text-left">
                <p className="text-sm font-medium text-red-700 mb-2">Error Details (first {Math.min(importResult.error_details.length, 5)}):</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1 max-h-40 overflow-y-auto">
                  {importResult.error_details.slice(0, 5).map((e, idx) => (
                    <p key={idx} className="text-xs text-red-600">
                      <span className="font-medium">Row {e.row}:</span> {e.error}
                    </p>
                  ))}
                  {importResult.error_details.length > 5 && (
                    <p className="text-xs text-red-500 italic">
                      ...and {importResult.error_details.length - 5} more errors
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={handleReset}>
                Import More
              </Button>
              <Button onClick={onComplete}>
                View Properties
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
